import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect, forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import findIndex from 'lodash/fp/findIndex';
import chunk from 'lodash/fp/chunk';
import { AnimatePresence, motion } from 'framer-motion';
import useAfterMount from '../../hooks/useAfterMount';
import useElementSize from '../../hooks/useElementSize';
import usePrevious from '../../hooks/usePrevious';
const DEFAULT_MIN_COLUMN_WITH = 300;
const DEFAULT_MIN_COLUMNS = 1;
const DEFAULT_MAX_COLUMNS = 5;
const DEFAULT_COLUMN_WRAPPER_TYPE = 'div';
const DEFAULT_ACTIVE_PAGE = 0;
const MIN_WITH_THRESHOLD = 10;
const ANIMATION_DURATION = 0.2;
const ANIMATION_NEXT = 'page';
const ANIMATION_BACK = 'pageBack';
const COLUMN_ITEM_CLASSNAME = 'ColumnItem';
const variants = {
    pageEnter: pageDirection => {
        return {
            x: pageDirection === ANIMATION_NEXT ? '60%' : '-60%',
            opacity: 0,
        };
    },
    pageCenter: () => {
        return {
            x: 0,
            opacity: 1,
        };
    },
};
const getFirstColumnItem = node => {
    if ([...node.classList].includes(COLUMN_ITEM_CLASSNAME)) {
        return node;
    }
    return getFirstColumnItem(node.children[0]);
};
const ResponsiveColumnStripe = forwardRef((props, ref) => {
    const { minColumnWith = DEFAULT_MIN_COLUMN_WITH, minColumns = DEFAULT_MIN_COLUMNS, maxColumns = DEFAULT_MAX_COLUMNS, className = '', buttonClassName = '', columnsWrapperClassName = '', onNextClick = () => { }, onPreviousClick = () => { }, stretchLastItems = true, asType = DEFAULT_COLUMN_WRAPPER_TYPE, activePage = DEFAULT_ACTIVE_PAGE, disableAnimation = false, children } = props, remainingProps = __rest(props, ["minColumnWith", "minColumns", "maxColumns", "className", "buttonClassName", "columnsWrapperClassName", "onNextClick", "onPreviousClick", "stretchLastItems", "asType", "activePage", "disableAnimation", "children"]);
    const ComponentType = asType;
    const [currentPage, setCurrentPage] = useState(activePage);
    const [columnsPerPage, setColumnsPerPage] = useState(maxColumns);
    const [enableInitialAnimation, setEnableInitialAnimation] = useState(false);
    const [isResizePage, setIsResizePage] = useState(false);
    // The base for reacting on changing width of the wrapping element.
    // It uses a ResizeObserver under the hood.
    const columnWrapperRef = useRef();
    const [columnWrapperWidth] = useElementSize(columnWrapperRef);
    const previousPage = usePrevious(currentPage);
    const animationDirection = currentPage > previousPage ? ANIMATION_NEXT : ANIMATION_BACK;
    useAfterMount(() => {
        setEnableInitialAnimation(true);
    });
    // Update active page from outside to be used as controlled component
    const [previousActivePage, setPreviousActivePage] = useState(activePage);
    if (Number.isFinite(activePage) && activePage !== previousActivePage) {
        setCurrentPage(activePage);
        setPreviousActivePage(activePage);
    }
    const updatePageOnColumnsSizeChange = (firstItem, columnsPerPage, children) => {
        // Split children in page chunks
        const itemChunks = chunk(columnsPerPage)(children);
        // find the currently rendered first item inside the chunks.
        // The found chunk index is the new page to render.
        // example of chunks => [['a', 'b', 'c'], ['d']]
        const targetPage = findIndex(chunk => chunk.some(chunkItem => { var _a; return chunkItem.key === ((_a = firstItem.attributes['data-key']) === null || _a === void 0 ? void 0 : _a.value); }))(itemChunks);
        if (targetPage !== currentPage) {
            setCurrentPage(targetPage);
        }
    };
    // When the container size changes, adapt the amount of columns to be rendered according
    // to the given min width of a single item. The new amount of columns also has to respect
    // the lower and upper limit. When updating the amount, the current page hse to be updated as well.
    useEffect(() => {
        if (!columnWrapperRef.current) {
            return;
        }
        const firstItem = getFirstColumnItem(columnWrapperRef.current.children[0]);
        if (!firstItem) {
            return;
        }
        // Get the width of the first column to calculate how many columns fit in one page
        // according to the given minWidth per column
        const columnWidth = firstItem.getBoundingClientRect().width;
        // Limit columns per page for given min and max values
        const allowForLessColumns = columnsPerPage - 1 >= minColumns;
        const allowForMoreColumns = columnsPerPage + 1 <= maxColumns;
        // The threshold is required to avoid jumping between bigger and smaller after the columns per page
        // has changed and it continues to resize
        const goSmaller = allowForLessColumns && columnWidth + MIN_WITH_THRESHOLD <= minColumnWith;
        const goBigger = allowForMoreColumns && (columnsPerPage + 1) * minColumnWith <= columnWrapperWidth;
        // During resizing, we don't want any animation
        setIsResizePage(true);
        if (goBigger) {
            const newValue = columnsPerPage + 1;
            setColumnsPerPage(newValue);
            updatePageOnColumnsSizeChange(firstItem, newValue, children);
            return;
        }
        if (goSmaller) {
            const newValue = columnsPerPage - 1;
            setColumnsPerPage(newValue);
            updatePageOnColumnsSizeChange(firstItem, newValue, children);
            return;
        }
    }, [columnWrapperWidth, columnWrapperRef.current, children, columnsPerPage]);
    const handlePrevClick = () => {
        setIsResizePage(false);
        const pageNumber = currentPage - 1;
        setCurrentPage(pageNumber);
        onPreviousClick(pageNumber, columnsPerPage);
    };
    const handleNextClick = () => {
        setIsResizePage(false);
        const pageNumber = currentPage + 1;
        setCurrentPage(pageNumber);
        onNextClick(pageNumber, columnsPerPage);
    };
    if (!children) {
        return null;
    }
    // Split the children/columns in chunks according to the current columns per page size
    // and get the current page chunk for rendering
    const columnsToDisplay = chunk(columnsPerPage)(children)[currentPage];
    const showPageButtons = minColumns < children.length;
    const disablePreviousPage = currentPage === 0;
    const disableNextPage = currentPage === Math.ceil(children.length / columnsPerPage) - 1;
    const wrapperClassName = classNames('ResponsiveColumnStripe', 'display-flex align-items-center', 'overflow-hidden', className);
    const baseButtonClassName = classNames('align-items-center', 'align-self-stretch', 'display-flex', 'hover-scale-105', 'hover-text-color-darkest', 'padding-10', 'cursor-pointer', 'text-color-darker', 'text-size-12');
    const disabledButtonClassName = 'pointer-events-none opacity-30';
    const previousButtonClassName = classNames('PreviousButton', baseButtonClassName, disablePreviousPage && disabledButtonClassName);
    const nextButtonClassName = classNames('NextButton', baseButtonClassName, disableNextPage && disabledButtonClassName);
    const mergedColumnsWrapperClassName = classNames('ColumnWrapper', 'flex-1-1', 'display-flex', columnsWrapperClassName);
    const columnClassName = disableNextPage && !stretchLastItems ? 'flex-0-1' : 'flex-1-1';
    // Use the given custom component type "div" or any other. Clone the children to inject certain
    // properties that are required to stretch the items and deal with their size.
    const columns = (_jsx(ComponentType, Object.assign({ className: mergedColumnsWrapperClassName }, { children: React.Children.map(columnsToDisplay, column => {
            var _a;
            return React.cloneElement(column, {
                className: `${COLUMN_ITEM_CLASSNAME} ${columnClassName} ${((_a = column.props) === null || _a === void 0 ? void 0 : _a.className) || ''}`,
                // style: { ...column.props?.style, minWidth: `${minColumnWith}px` },
                'data-key': column.key,
            });
        }) })));
    return (_jsxs("div", Object.assign({}, remainingProps, { ref: ref, className: wrapperClassName }, { children: [showPageButtons && (_jsx("div", Object.assign({ className: previousButtonClassName, onClick: handlePrevClick }, { children: _jsx("span", { className: 'rioglyph rioglyph-chevron-left' }) }))), _jsxs("div", Object.assign({ className: 'ColumnsArea flex-1-1 overflow-hidden', ref: columnWrapperRef }, { children: [disableAnimation || isResizePage ? columns : null, !disableAnimation && !isResizePage && (_jsx(AnimatePresence, Object.assign({ exitBeforeEnter: true, custom: animationDirection }, { children: _jsx(motion.div, Object.assign({ variants: variants, initial: enableInitialAnimation ? 'pageEnter' : false, animate: 'pageCenter', custom: animationDirection, transition: { duration: ANIMATION_DURATION } }, { children: columns }), currentPage) })))] })), showPageButtons && (_jsx("div", Object.assign({ className: nextButtonClassName, onClick: handleNextClick }, { children: _jsx("span", { className: 'rioglyph rioglyph-chevron-right' }) })))] })));
});
export default ResponsiveColumnStripe;
ResponsiveColumnStripe.propTypes = {
    minColumnWith: PropTypes.number,
    minColumns: PropTypes.number,
    maxColumns: PropTypes.number,
    className: PropTypes.string,
    buttonClassName: PropTypes.string,
    columnsWrapperClassName: PropTypes.string,
    onNextClick: PropTypes.func,
    onPreviousClick: PropTypes.func,
    stretchLastItems: PropTypes.bool,
    asType: PropTypes.string,
    activePage: PropTypes.number,
    disableAnimation: PropTypes.bool,
};
